/**
 * Binary raster operations on a row-major cell grid. Row 0 is the bottom row, so cell
 * coordinates run the same way as CAD model coordinates (Y up).
 */

export interface GridShape {
  readonly width: number;
  readonly height: number;
}

export type Mask = Uint8Array;

export interface CellPoint {
  readonly x: number;
  readonly y: number;
}

export function emptyMask(shape: GridShape): Mask {
  return new Uint8Array(shape.width * shape.height);
}

/** Marks every cell a segment in cell coordinates passes through. */
export function rasterSegment(
  mask: Mask,
  shape: GridShape,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): void {
  const steps = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0) * 2));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const cx = Math.floor(x0 + (x1 - x0) * t);
    const cy = Math.floor(y0 + (y1 - y0) * t);
    if (cx >= 0 && cy >= 0 && cx < shape.width && cy < shape.height) {
      mask[cy * shape.width + cx] = 1;
    }
  }
}

/** Square dilation of the given radius, separable, linear in the cell count. */
export function dilate(mask: Mask, shape: GridShape, radius: number): Mask {
  if (radius <= 0) return mask.slice();
  const { width, height } = shape;
  const rows = new Uint8Array(mask.length);
  const prefix = new Int32Array(Math.max(width, height) + 1);
  for (let y = 0; y < height; y++) {
    const row = y * width;
    for (let x = 0; x < width; x++) prefix[x + 1] = prefix[x]! + mask[row + x]!;
    for (let x = 0; x < width; x++) {
      const lo = Math.max(0, x - radius);
      const hi = Math.min(width, x + radius + 1);
      if (prefix[hi]! - prefix[lo]! > 0) rows[row + x] = 1;
    }
  }
  const out = new Uint8Array(mask.length);
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) prefix[y + 1] = prefix[y]! + rows[y * width + x]!;
    for (let y = 0; y < height; y++) {
      const lo = Math.max(0, y - radius);
      const hi = Math.min(height, y + radius + 1);
      if (prefix[hi]! - prefix[lo]! > 0) out[y * width + x] = 1;
    }
  }
  return out;
}

export function invert(mask: Mask): Mask {
  const out = new Uint8Array(mask.length);
  for (let i = 0; i < mask.length; i++) out[i] = mask[i] === 1 ? 0 : 1;
  return out;
}

/** Morphological closing: fills gaps narrower than twice the radius. */
export function close(mask: Mask, shape: GridShape, radius: number): Mask {
  return invert(dilate(invert(dilate(mask, shape, radius)), shape, radius));
}

/** Everything the outside cannot reach without crossing the mask. */
export function fillHoles(mask: Mask, shape: GridShape): Mask {
  const { width, height } = shape;
  const outside = new Uint8Array(mask.length);
  const queue = new Int32Array(mask.length);
  let tail = 0;
  const seed = (i: number) => {
    if (mask[i] === 0 && outside[i] === 0) {
      outside[i] = 1;
      queue[tail++] = i;
    }
  };
  for (let x = 0; x < width; x++) {
    seed(x);
    seed((height - 1) * width + x);
  }
  for (let y = 0; y < height; y++) {
    seed(y * width);
    seed(y * width + width - 1);
  }
  for (let head = 0; head < tail; head++) {
    const i = queue[head]!;
    const x = i % width;
    if (x > 0) seed(i - 1);
    if (x < width - 1) seed(i + 1);
    if (i >= width) seed(i - width);
    if (i < mask.length - width) seed(i + width);
  }
  return invert(outside);
}

export interface Components {
  /** Component id per cell, -1 outside the mask. */
  readonly labels: Int32Array;
  readonly sizes: readonly number[];
}

/** 4-connected components. */
export function components(mask: Mask, shape: GridShape): Components {
  const { width } = shape;
  const labels = new Int32Array(mask.length).fill(-1);
  const sizes: number[] = [];
  const queue = new Int32Array(mask.length);
  for (let start = 0; start < mask.length; start++) {
    if (mask[start] !== 1 || labels[start] !== -1) continue;
    const id = sizes.length;
    let tail = 0;
    labels[start] = id;
    queue[tail++] = start;
    for (let head = 0; head < tail; head++) {
      const i = queue[head]!;
      const x = i % width;
      const visit = (n: number) => {
        if (mask[n] === 1 && labels[n] === -1) {
          labels[n] = id;
          queue[tail++] = n;
        }
      };
      if (x > 0) visit(i - 1);
      if (x < width - 1) visit(i + 1);
      if (i >= width) visit(i - width);
      if (i < mask.length - width) visit(i + width);
    }
    sizes.push(tail);
  }
  return { labels, sizes };
}

function signedArea(loop: readonly CellPoint[]): number {
  let area = 0;
  for (let i = 0; i < loop.length; i++) {
    const a = loop[i]!;
    const b = loop[(i + 1) % loop.length]!;
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
}

/** Drops vertices that sit on a straight run between their neighbours. */
export function dropCollinear(points: readonly CellPoint[], closed: boolean): CellPoint[] {
  const out: CellPoint[] = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p = points[i]!;
    if (!closed && (i === 0 || i === n - 1)) {
      out.push(p);
      continue;
    }
    const prev = points[(i - 1 + n) % n]!;
    const next = points[(i + 1) % n]!;
    const cross = (p.x - prev.x) * (next.y - p.y) - (p.y - prev.y) * (next.x - p.x);
    if (cross !== 0) out.push(p);
  }
  return out;
}

/**
 * The outer boundary of each 4-connected piece of the mask, as counter-clockwise loops of
 * cell-corner coordinates. Holes are not returned.
 */
export function outerLoops(mask: Mask, shape: GridShape): CellPoint[][] {
  const { width, height } = shape;
  const stride = width + 1;
  // Directed boundary edges with the mask on their left, keyed by start corner. A corner
  // where two cells touch diagonally has two outgoing edges.
  const outgoing = new Map<number, number[]>();
  const add = (x0: number, y0: number, x1: number, y1: number) => {
    const from = y0 * stride + x0;
    const list = outgoing.get(from);
    const to = y1 * stride + x1;
    if (list === undefined) outgoing.set(from, [to]);
    else list.push(to);
  };
  const inside = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < width && y < height && mask[y * width + x] === 1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!inside(x, y)) continue;
      if (!inside(x, y - 1)) add(x, y, x + 1, y);
      if (!inside(x + 1, y)) add(x + 1, y, x + 1, y + 1);
      if (!inside(x, y + 1)) add(x + 1, y + 1, x, y + 1);
      if (!inside(x - 1, y)) add(x, y + 1, x, y);
    }
  }

  const loops: CellPoint[][] = [];
  for (const [startKey] of outgoing) {
    while ((outgoing.get(startKey)?.length ?? 0) > 0) {
      const loop: CellPoint[] = [];
      let current = startKey;
      let dx = 0;
      let dy = 0;
      do {
        const cx = current % stride;
        const cy = Math.floor(current / stride);
        loop.push({ x: cx, y: cy });
        const options = outgoing.get(current);
        if (options === undefined || options.length === 0) break;
        // At a diagonal pinch, turning left keeps the two cells in separate loops, which is
        // what 4-connectivity means.
        let pick = 0;
        if (options.length > 1) {
          let best = -Infinity;
          options.forEach((to, index) => {
            const tx = (to % stride) - cx;
            const ty = Math.floor(to / stride) - cy;
            const turn = dx * ty - dy * tx;
            if (turn > best) {
              best = turn;
              pick = index;
            }
          });
        }
        const next = options.splice(pick, 1)[0]!;
        dx = (next % stride) - cx;
        dy = Math.floor(next / stride) - cy;
        current = next;
      } while (current !== startKey);
      if (loop.length >= 4 && signedArea(loop) > 0) loops.push(dropCollinear(loop, true));
    }
  }
  return loops;
}

export function loopArea(loop: readonly CellPoint[]): number {
  return Math.abs(signedArea(loop));
}
